import { useAuthStore } from '@/stores/auth';
import type {
	UserDTO,
	CompanyDTO,
	EmployeeDTO,
	LeaveRequestDTO,
	ReimbursementClaimDTO
} from '@employer-on-record/shared';

const API_BASE_URL = '/api';

interface RequestOptions {
	method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	body?: unknown;
}
interface ApiErrorEnvelope { error?: { code?: string; message: string } }

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
	const { method = 'GET', body } = options;
	const token = useAuthStore.getState().token;
	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	if (token) headers['Authorization'] = `Bearer ${token}`;
	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method, headers, body: body ? JSON.stringify(body) : undefined
	});
	const data = (await response.json()) as T & ApiErrorEnvelope;
	if (!response.ok) {
		if (response.status === 401) { useAuthStore.getState().logout(); window.location.href = '/login'; }
		throw new Error(data.error?.message || 'Request failed');
	}
	return data;
}

interface Envelope<T> { success: boolean; data: T; }

type CompanyWithCounts = CompanyDTO & { employeeCount: number; hrCount: number };
type HrUserWithCompany = UserDTO & { company?: { id: string; name: string } | null };

export const authApi = {
	login: (email: string, password: string) =>
		request<Envelope<{ user: UserDTO; token: string }>>('/auth/login', { method: 'POST', body: { email, password } }),
	me: () => request<Envelope<UserDTO>>('/auth/me')
};

export const dashboardApi = {
	admin: () => request<Envelope<{
		companyCount: number;
		hrCount: number;
		employeeCount: number;
		pendingLeave: number;
		pendingReimbursement: number;
	}>>('/dashboard/admin')
};

export const companiesApi = {
	list: () => request<Envelope<CompanyWithCounts[]>>('/companies'),
	get: (id: string) => request<Envelope<CompanyDTO>>(`/companies/${id}`),
	create: (body: Partial<CompanyDTO>) =>
		request<Envelope<CompanyDTO>>('/companies', { method: 'POST', body }),
	update: (id: string, body: Partial<CompanyDTO>) =>
		request<Envelope<CompanyDTO>>(`/companies/${id}`, { method: 'PATCH', body })
};

export const adminApi = {
	hrUsers: (companyId?: string) =>
		request<Envelope<HrUserWithCompany[]>>(`/admin/hr-users${companyId ? `?companyId=${companyId}` : ''}`),
	createHrUser: (body: { companyId: string; email: string; name: string; department?: string; position?: string; password?: string }) =>
		request<Envelope<{ user: UserDTO; initialPassword: string | null }>>('/admin/hr-users', { method: 'POST', body }),
	approverUsers: (companyId?: string) =>
		request<Envelope<HrUserWithCompany[]>>(`/admin/approvers${companyId ? `?companyId=${companyId}` : ''}`),
	createApproverUser: (body: { companyId: string; email: string; name: string; department?: string; position?: string; password?: string }) =>
		request<Envelope<{ user: UserDTO; initialPassword: string | null }>>('/admin/approvers', { method: 'POST', body }),
	createEmployee: (body: Record<string, unknown>) =>
		request<Envelope<{ employee: EmployeeDTO; user: UserDTO; initialPassword: string | null }>>('/admin/employees', { method: 'POST', body }),
	users: (companyId?: string, role?: string) =>
		request<Envelope<UserDTO[]>>(`/admin/users${companyId || role ? '?' : ''}${companyId ? `companyId=${companyId}` : ''}${companyId && role ? '&' : ''}${role ? `role=${role}` : ''}`),
	blockUser: (id: string, isBlocked: boolean) =>
		request<Envelope<UserDTO>>(`/admin/users/${id}/block`, { method: 'PATCH', body: { isBlocked } }),
	setUserActive: (id: string, isActive: boolean) =>
		request<Envelope<UserDTO>>(`/admin/users/${id}/active`, { method: 'PATCH', body: { isActive } }),
	updateUser: (id: string, body: { name?: string; email?: string; department?: string | null; position?: string | null; isActive?: boolean }) =>
		request<Envelope<UserDTO>>(`/admin/users/${id}`, { method: 'PATCH', body })
};

export const employeesAdminApi = {
	update: (id: string, body: Record<string, unknown>) =>
		request<Envelope<EmployeeDTO>>(`/employees/${id}`, { method: 'PATCH', body })
};

export const employeesApi = {
	list: (companyId?: string) =>
		request<Envelope<EmployeeDTO[]>>(`/employees${companyId ? `?companyId=${companyId}` : ''}`)
};

export interface EmployeeRequestDTO {
	id: string;
	companyId: string;
	requestedById: string;
	status: 'pending' | 'approved' | 'rejected';
	decidedById: string | null;
	decidedAt: string | null;
	rejectionReason: string | null;
	createdEmployeeId: string | null;
	employeeNumber: string;
	fullName: string;
	email: string;
	department: string | null;
	position: string | null;
	approver1Id: string | null;
	approver2Id: string | null;
	approver3Id: string | null;
	approver4Id: string | null;
	employmentType: string;
	workLocation: string;
	startDate: string;
	createdAt: string;
	updatedAt: string;
	requestedBy?: { id: string; name: string; email: string } | null;
	decidedBy?: { id: string; name: string; email: string } | null;
}

export const employeeRequestsApi = {
	list: (companyId?: string, status?: string) => {
		const params = new URLSearchParams();
		if (companyId) params.set('companyId', companyId);
		if (status) params.set('status', status);
		const qs = params.toString();
		return request<Envelope<EmployeeRequestDTO[]>>(`/employee-requests${qs ? `?${qs}` : ''}`);
	},
	approve: (id: string, password?: string) =>
		request<Envelope<{ request: EmployeeRequestDTO; employee: EmployeeDTO; user: UserDTO; initialPassword: string | null }>>(
			`/employee-requests/${id}/approve`,
			{ method: 'POST', body: password ? { password } : undefined }
		),
	reject: (id: string, reason: string) =>
		request<Envelope<EmployeeRequestDTO>>(`/employee-requests/${id}/reject`, { method: 'POST', body: { reason } })
};

export const leaveApi = {
	list: (companyId?: string, status?: string) => {
		const params = new URLSearchParams();
		if (companyId) params.set('companyId', companyId);
		if (status) params.set('status', status);
		const qs = params.toString();
		return request<Envelope<LeaveRequestDTO[]>>(`/leave${qs ? `?${qs}` : ''}`);
	}
};

export const reimbursementsApi = {
	list: (companyId?: string, status?: string) => {
		const params = new URLSearchParams();
		if (companyId) params.set('companyId', companyId);
		if (status) params.set('status', status);
		const qs = params.toString();
		return request<Envelope<ReimbursementClaimDTO[]>>(`/reimbursements${qs ? `?${qs}` : ''}`);
	},
	reimburse: (id: string) =>
		request<Envelope<ReimbursementClaimDTO>>(`/reimbursements/${id}/reimburse`, { method: 'POST' })
};
